import React from 'react';
import { RegisterOptions } from 'react-hook-form';
import { FormStructure } from '@europrocurement/flexy-form';
import { useTiersService } from '@europrocurement/l2d-domain';
import { Box, Typography, useTheme } from '@mui/material';
import ContactChip from '../../Dossiers/components/shared/ContactChip/ContactChip';
import { type ContactItem } from '../../Dossiers/hooks';

type RecipientsInputFormStructureType = {
    idPrescriber: number;
    inputName: string;
    inputLabel: string;
    listboxTitle: string;
    placeHolder: string;
    rules?: RegisterOptions;
};

type NonEditableArrayDataType = {
    label: string;
    name: string;
    arrayValues: string[];
};
type NonEditableSingleDataType = {
    label: string;
    name: string;
    value: string;
};
type RecipientDataType = {
    email: string;
    firstName: string;
    lastName: string;
};
type NonEditableRecipientsDataType = {
    label: string;
    name: string;
    arrayValues: RecipientDataType[];
};

const useMailInputsFormStructure = () => {
    const { contactsApi } = useTiersService();
    const { palette } = useTheme();

    const createRecipientsInput = ({
        idPrescriber,
        inputName = 'recipients',
        inputLabel = 'Envoyer à',
        listboxTitle = 'Contacts',
        placeHolder = 'Rechercher un contact',
        rules,
    }: RecipientsInputFormStructureType) =>
        ({
            type: 'autocomplete',
            name: inputName,
            inputlabel: inputLabel,
            listboxTitle,
            placeholder: placeHolder,
            rules,
            onSearch: (term): Promise<Array<ContactItem>> =>
                new Promise((resolve, reject) => {
                    let searchTimeout: number | undefined;

                    clearTimeout(searchTimeout);
                    searchTimeout = setTimeout(() => {
                        contactsApi
                            .getContactsContactsCollection({
                                xIdSociete: 12,
                                q: term,
                                tiersId2: [idPrescriber],
                            })
                            .then((res) => {
                                const items = res.data['hydra:member'] || [];

                                resolve(
                                    items
                                        .map((item) => ({
                                            id: item?.id as number,
                                            firstName: item?.nomContact as string,
                                            lastName: item?.prenomContact as string,
                                            emailAddress: item?.emailsContact.find(
                                                (emailAddress) => emailAddress?.defaut === true,
                                            )?.email as string,
                                        }))
                                        .filter(
                                            (item) =>
                                                item.firstName &&
                                                item.lastName &&
                                                item.emailAddress,
                                        ),
                                );
                            })
                            .catch((err) => reject(err));
                    }, 300);
                }),
            getOptionValue: (option: ContactItem) => option.id,

            getOptionLabel: (option: ContactItem) => (
                <ContactChip
                    firstName={option.firstName}
                    lastName={option.lastName}
                />
            ),
        }) as FormStructure;

    const createNonEditableRecipientsDataInput = ({
        label,
        name,
        arrayValues,
    }: NonEditableRecipientsDataType) =>
        ({
            type: 'customItem',
            name,
            renderField: () => (
                <Box sx={{ display: 'grid', gap: '4px' }}>
                    <Typography variant="l2dlabel">{label}</Typography>

                    <Box
                        sx={{
                            border: `${palette.grey[300]}  1px solid`,
                            borderRadius: '12px',
                            gap: '8px',
                            padding: '8px 20px',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                padding: '8px',
                                gap: '8px',
                            }}
                        >
                            {arrayValues.map((contact) => (
                                <ContactChip
                                    key={contact.email}
                                    firstName={contact.firstName}
                                    lastName={contact.lastName}
                                    sx={{
                                        backgroundColor: palette.primary.light,
                                        borderRadius: '12px',
                                        padding: '2px 8px',
                                        gap: '4px',
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>
                </Box>
            ),
        }) as FormStructure;

    const createNonEditableArrayDataInput = ({
        label,
        name,
        arrayValues,
    }: NonEditableArrayDataType) =>
        ({
            type: 'customItem',
            name,
            renderField: () => (
                <Box sx={{ display: 'grid', gap: '4px' }}>
                    <Typography variant="l2dlabel">{label}</Typography>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', padding: '8px', gap: '8px' }}>
                        {arrayValues.map((value, i) => (
                            <Box
                                // eslint-disable-next-line react/no-array-index-key
                                key={i}
                                data-testid="mail-data-from-array"
                                sx={{
                                    gap: '8px',
                                    border: `1px solid ${palette.grey[400]}`,
                                    borderRadius: '12px',
                                    padding: '8px',
                                }}
                            >
                                {value}
                            </Box>
                        ))}
                    </Box>
                </Box>
            ),
        }) as FormStructure;

    const createNonEditableSingleDataInput = ({ label, name, value }: NonEditableSingleDataType) =>
        ({
            type: 'customItem',
            name,
            renderField: () => (
                <Box sx={{ display: 'grid', gap: '4px' }}>
                    <Typography variant="l2dlabel">{label}</Typography>

                    <Box
                        sx={{
                            border: `${palette.grey[300]}  1px solid`,
                            borderRadius: '12px',
                            gap: '8px',
                            padding: '8px 20px',
                        }}
                    >
                        <Box
                            data-testid="mail-data-from-singleValue"
                            sx={{
                                gap: '8px',
                                borderRadius: '12px',
                                padding: '8px',
                                width: '100%',
                                whiteSpace: 'pre-wrap',
                            }}
                        >
                            {value}
                        </Box>
                    </Box>
                </Box>
            ),
        }) as FormStructure;

    return {
        createRecipientsInput,
        createNonEditableRecipientsDataInput,
        createNonEditableArrayDataInput,
        createNonEditableSingleDataInput,
    };
};

export default useMailInputsFormStructure;
